import React, { useEffect, useRef } from "react";
import { toast } from "sonner";
import { RotateCcw, X } from "lucide-react";
import api from "./api";

const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// MT-11B.7: a draft carries CLINICAL form state only. Authentication, tenant
// membership, role and prescriber linkage (doctor_id / delegate_doctor_ids /
// signed_by_doctor_id) are never written into or restored from a draft — the
// server-resolved auth context is the only source for those.
export const DRAFT_CLINICAL_KEYS = [
  "vitals", "chief", "history", "known", "complications", "allergyFlags", "patientFlags",
  "allergyText", "investigations", "diagnosis", "medicines", "advice", "adviceSummary",
  "adviceView", "adviceStyle", "followUpDays", "nextInv", "safetyOverrides", "sjsCulprit",
];
export const DRAFT_FORBIDDEN_KEYS = [
  "user", "me", "role", "doctor_id", "delegate_doctor_ids", "signedByDoctorId", "signed_by_doctor_id",
  "tenant_id", "tenant", "membership", "session_token", "entitlements", "email", "user_id",
];

export function sanitizeDraftState(state) {
  if (!state || typeof state !== "object") return null;
  const out = {};
  for (const k of DRAFT_CLINICAL_KEYS) if (k in state) out[k] = state[k];
  return out;
}

/** Remove all `draft-visit-*` entries older than 30 days. Returns number pruned. */
export function pruneOldDrafts(maxAge = DRAFT_MAX_AGE_MS) {
  let n = 0;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("draft-visit-")) keys.push(k);
    }
    const cutoff = Date.now() - maxAge;
    for (const k of keys) {
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const d = JSON.parse(raw);
        if (!d?.savedAt || d.savedAt < cutoff) {
          localStorage.removeItem(k);
          n += 1;
        }
      } catch {
        localStorage.removeItem(k);
        n += 1;
      }
    }
    if (n > 0) window.dispatchEvent(new Event("drafts-changed"));
  } catch (e) { console.debug("[silent]", e); }
  return n;
}

/** True when the doctor has actually entered something worth keeping. */
export function draftHasContent(state) {
  if (!state) return false;
  const v = state.vitals || {};
  return !!(
    (state.chief && state.chief.trim()) ||
    Object.values(state.history || {}).some((x) => x && String(x).trim()) ||
    (state.known || []).length ||
    (state.medicines || []).some((m) => m.brand_name) ||
    (state.investigations || []).some((i) => i.test_name) ||
    Object.values(v).some((x) => x !== "" && x != null) ||
    (state.diagnosis && (state.diagnosis.primary || state.diagnosis.secondary)) ||
    (state.advice && state.advice.trim())
  );
}

/** Explicit cloud save — used by the "Draft" button. */
export async function saveDraftServer(pid, state, waitingFor) {
  const body = { state: sanitizeDraftState(state) };
  if (waitingFor !== undefined) body.waiting_for = waitingFor;
  const r = await api.put(`/visit-drafts/${pid}`, body);
  window.dispatchEvent(new Event("drafts-changed"));
  return r.data;
}

/**
 * Draft autosave — CLOUD-FIRST (works across clinic/home PCs).
 * - localStorage `draft-visit-<pid>` remains the instant same-device fallback.
 * - State also syncs to the server (5 s debounce) whenever there is content.
 * - On mount: fetches the server draft, compares with local, restores the
 *   NEWER of the two (toast tells the doctor when it came from the cloud).
 * - `clear()` removes both local and server copies (called after final save).
 */
export function useDraft(pid, state, onRestore) {
  const key = `draft-visit-${pid}`;
  const restoredRef = useRef(false);

  // Local save on state change (fast crash safety)
  useEffect(() => {
    if (!pid) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), state: sanitizeDraftState(state) }));
        window.dispatchEvent(new Event("drafts-changed"));
      } catch (e) { console.debug("[silent]", e); }
    }, 800);
    return () => clearTimeout(t);
  }, [key, pid, state]);

  // Cloud sync (debounced, only when the form has real content, never in edit mode)
  useEffect(() => {
    if (!pid) return;
    if (new URLSearchParams(window.location.search).get("edit")) return;
    if (!draftHasContent(state)) return;
    const t = setTimeout(() => {
      api.put(`/visit-drafts/${pid}`, { state: sanitizeDraftState(state) }).catch((e) => console.debug("[silent]", e));
    }, 5000);
    return () => clearTimeout(t);
  }, [pid, state]);

  // Restore once on mount — newest of local vs cloud wins
  useEffect(() => {
    if (!pid || restoredRef.current) return;
    restoredRef.current = true;
    let localDraft = null;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.savedAt && (Date.now() - d.savedAt) > DRAFT_MAX_AGE_MS) {
          localStorage.removeItem(key);
          window.dispatchEvent(new Event("drafts-changed"));
        } else if (d?.state) {
          localDraft = d;
        }
      }
    } catch (e) { console.debug("[silent]", e); }
    (async () => {
      let serverDraft = null;
      try {
        const r = await api.get(`/visit-drafts/${pid}`);
        if (r.data?.draft?.state) {
          serverDraft = { state: r.data.draft.state, savedAt: new Date(r.data.draft.saved_at).getTime() || 0 };
        }
      } catch (e) { console.debug("[silent]", e); }
      const useServer = serverDraft && (!localDraft || serverDraft.savedAt > (localDraft.savedAt || 0));
      const pick = useServer ? serverDraft : localDraft;
      if (pick?.state) {
        onRestore(sanitizeDraftState(pick.state), new Date(pick.savedAt));
        if (useServer) toast.info("Draft loaded from cloud — saved earlier on another PC or session");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  const clear = () => {
    try {
      localStorage.removeItem(key);
      window.dispatchEvent(new Event("drafts-changed"));
    } catch (e) { console.debug("[silent]", e); }
    api.delete(`/visit-drafts/${pid}`).catch((e) => console.debug("[silent]", e));
  };

  return { clear };
}

export function DraftBanner({ savedAt, onDiscard }) {
  if (!savedAt) return null;
  return (
    <div className="mb-3 alert-info rounded-md px-3 py-2 flex items-center justify-between text-xs" data-testid="draft-restored-banner">
      <div className="flex items-center gap-2">
        <RotateCcw size={12} />
        Draft restored from {new Date(savedAt).toLocaleString()} — keep typing to overwrite, or discard. Drafts auto-expire after 30 days.
      </div>
      <button onClick={onDiscard} className="p-1 rounded hover:bg-blue-100" data-testid="discard-draft-btn" title="Discard draft">
        <X size={12} />
      </button>
    </div>
  );
}
